import {
  toWSDateString,
  WSCard,
  WSDataItem,
  WSGrid,
  WSInfoBox,
  WSList,
  WSPillProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";

import { selectorPayrollRunStatusPill } from "../../selectors/selectorPayrollRunStatusPill";
import { PayrollRunSummary } from "../../types";

const OFF_CYCLE_PILL: WSPillProps = {
  theme: "neutral",
  badge: true,
  text: "Off-cycle"
};

const PAY_PERIOD_TOOLTIP = "The date range this payroll covers.";
const PAYSTUBS_TOOLTIP = "Number of paystubs generated for this payroll.";

type Props = {
  payrollRun: PayrollRunSummary;
  goToDetails: () => void;
};

export const PayrollRunCard: React.FC<Props> = ({
  payrollRun,
  goToDetails
}) => {
  const liabilityBreakdown = [
    { label: "Gross amount", amount: payrollRun.grossAmount },
    { label: "Payer taxes", amount: payrollRun.payerTaxes },
    { label: "Benefits", amount: payrollRun.benefits }
  ];

  return (
    <WSCard
      header={{
        label: payrollRun.payPeriodLabel,
        value: payrollRun.isOffCycle ? { pill: OFF_CYCLE_PILL } : undefined
      }}
      onClick={() => {
        goToDetails();
      }}
    >
      <WSGrid gutter="L">
        <WSGrid.Item span={{ s: "12", l: "6" }}>
          <WSList gap="S">
            <WSInfoBox>
              <WSText.Paragraph color="gray600" mb="XS" weight="medium">
                Liability amount
              </WSText.Paragraph>
              <WSText.Heading1 color="gray500" weight="book">
                {formatMoney(payrollRun.liabilityAmount, Currency.USD)}
              </WSText.Heading1>
            </WSInfoBox>

            <WSInfoBox>
              <WSList gap="S">
                {liabilityBreakdown.map(item => (
                  <WSDataItem
                    key={item.label}
                    inline
                    label={item.label}
                    value={formatMoney(item.amount, Currency.USD)}
                  />
                ))}
              </WSList>
            </WSInfoBox>
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ s: "12", l: "6" }}>
          <WSGrid gutter="XL">
            <WSGrid.Item span="6">
              <WSDataItem label="Payroll ID" value={payrollRun.payrollId} />
            </WSGrid.Item>

            <WSGrid.Item span="6">
              <WSDataItem
                label={{ text: "Pay period", tooltip: PAY_PERIOD_TOOLTIP }}
                value={payrollRun.payPeriodRange}
              />
            </WSGrid.Item>

            {payrollRun.finalizationDate && (
              <WSGrid.Item span="6">
                <WSDataItem
                  label="Finalization date"
                  value={toWSDateString(
                    payrollRun.finalizationDate,
                    "monthDayYear"
                  )}
                />
              </WSGrid.Item>
            )}

            <WSGrid.Item span="6">
              <WSDataItem
                hideValue
                label="Status"
                pill={selectorPayrollRunStatusPill(payrollRun.status)}
              />
            </WSGrid.Item>

            <WSGrid.Item span="6">
              <WSDataItem
                label={{ text: "No. of paystubs", tooltip: PAYSTUBS_TOOLTIP }}
                value={`${payrollRun.paystubsCount}`}
              />
            </WSGrid.Item>
          </WSGrid>
        </WSGrid.Item>
      </WSGrid>
    </WSCard>
  );
};
